import {
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
} from "downshift";
import {
  MultiSelectFieldGroupByValue,
  MultiSelectFieldOption,
  MultiSelectFieldPinnedOptions,
} from "../types";
import { SelectItem } from "../../../../internal/types/selectFieldInternalTypes";
import { CheckState } from "../../../../types";
import { useMemo } from "react";
import { useDownshiftEnvironment } from "../../../../internal/hooks";
import { useProcessedOptions } from "../../../../internal/hooks/useProcessedOptions";
import { buildSelectItems } from "../../../../internal/functions/buildSelectItems";
import { useToggleSelection } from "../../../../internal/hooks/useToggleSelection";

export type UseSelectModeMultipleOptions = {
  options: MultiSelectFieldOption[];
  pinned?: MultiSelectFieldPinnedOptions;
  groupToString?: (groupValue: MultiSelectFieldGroupByValue) => string;
  groupSorter?: (
    a: MultiSelectFieldGroupByValue,
    b: MultiSelectFieldGroupByValue,
  ) => number;
  selectAll?: {
    label: string;
    onClick: () => void;
    checkState: CheckState;
  };
  selectedOptions: MultiSelectFieldOption[];
  onSelectedOptionsChange: (options: MultiSelectFieldOption[]) => void;
  onIsOpenChange?: (changes: { isOpen?: boolean }) => void;
  displayAs: "popover" | "dialog";
  disabled: boolean;
  virtualize?: boolean;
  onScrollToHighlight?: (index: number) => void;
};

/**
 * Wraps downshift's useSelect for non-searchable multi-select fields.
 * Keeps menu open after selection and handles bulk actions.
 */
export const useSelectModeMultiple = ({
  options,
  pinned,
  groupToString,
  groupSorter,
  selectAll,
  selectedOptions,
  onSelectedOptionsChange,
  onIsOpenChange,
  displayAs = "popover",
  disabled,
  virtualize = false,
  onScrollToHighlight,
}: UseSelectModeMultipleOptions) => {
  const { pinnedSections, ungroupedItems, groupSections } = useProcessedOptions(
    {
      options,
      pinned,
      searchValue: "",
      groupToString,
      groupSorter,
    },
  );

  // Compute bulk action items for non-searchable mode (only select-all)
  const bulkActionItems = useMemo(() => {
    const items: SelectItem[] = [];
    if (selectAll) {
      items.push({
        id: "select-all",
        type: "select-all",
        original: {
          id: "select-all",
          label: selectAll.label ?? "Select All",
        },
        checkState: selectAll.checkState,
      });
    }
    return items;
  }, [selectAll]);

  const { items, sectionsMeta } = useMemo(
    () =>
      buildSelectItems({
        prefixItems: bulkActionItems,
        pinnedSections,
        groupSections,
        ungroupedItems,
      }),
    [bulkActionItems, pinnedSections, groupSections, ungroupedItems],
  );

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { handleItemClick } = useToggleSelection({
    options,
    pinnedSections,
    selectedOptions,
    onSelectedOptionsChange,
  });

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    closeMenu,
    openMenu,
  } = useSelect({
    isItemDisabled: (item) => {
      if (!item) return false;
      return (disabled || item.disabled) ?? false;
    },
    environment,
    selectedItem: null,
    onSelectedItemChange: ({
      selectedItem: si,
    }: {
      selectedItem: SelectItem | null;
    }) => {
      if (si) {
        if (si.type === "select-all") {
          selectAll?.onClick();
        } else if (
          si.type === "option" ||
          si.type === "pinned-option" ||
          si.type === "grouped-option"
        ) {
          handleItemClick(si.original);
        }
      }
    },
    onHighlightedIndexChange: ({ type, highlightedIndex: idx }) => {
      if (type !== useSelect.stateChangeTypes.ItemMouseMove && idx >= 0) {
        onScrollToHighlight?.(idx);
      }
    },
    onIsOpenChange,
    items: items,
    itemToString(item: SelectItem | null) {
      return item?.original.label ?? "";
    },
    stateReducer: (state, actionAndChanges) =>
      stateReducer(state, actionAndChanges, displayAs),
    ...(virtualize && { scrollIntoView: () => {} }),
  });

  return {
    wrapperDivRef,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    isOpen,
    items,
    sectionsMeta,
    closeMenu,
    openMenu,
    toggleMenu: () => (isOpen ? closeMenu() : openMenu()),
    selectedOptions,
  };
};

function stateReducer(
  state: UseSelectState<SelectItem>,
  actionAndChanges: UseSelectStateChangeOptions<SelectItem>,
  displayAs: "popover" | "dialog",
) {
  const { type, changes } = actionAndChanges;
  switch (type) {
    case useSelect.stateChangeTypes.ToggleButtonBlur:
      if (displayAs === "dialog" && state.isOpen) {
        return { ...changes, isOpen: state.isOpen, selectedItem: null };
      }
      return { ...changes, selectedItem: null };
    case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
    case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
    case useSelect.stateChangeTypes.ItemClick:
      return {
        ...changes,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };
    case useSelect.stateChangeTypes.ToggleButtonKeyDownEscape:
    case useSelect.stateChangeTypes.FunctionCloseMenu:
      return changes;
    case useSelect.stateChangeTypes.ToggleButtonClick:
      if (displayAs === "dialog" && state.isOpen) {
        return { ...changes, isOpen: state.isOpen };
      }
      return changes;
    default:
      return changes;
  }
}
